import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';

const CATEGORIES = ['bearings', 'fasteners', 'electrical', 'hydraulics', 'avionics', 'raw_materials', 'consumables'];
const INCOTERMS = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];

const blankContact = { name: '', email: '', phone: '' };
// Our own standing for a supplier (#17). Gates the scorecard — a Suspended
// supplier cannot rank near the top however responsive they are.
const APPROVAL_STATUSES = ['Approved', 'Conditional', 'Probation', 'Suspended', 'Unapproved'];

const blankForm = {
  name: '',
  category: [],
  defaultIncoterm: 'FCA',
  defaultLocation: '',
  // Drives the freight lane and duty rate for landed cost (#13).
  defaultCountryOfOrigin: '',
  // Certification + approval (#17).
  as9120CertNumber: '',
  as9120ExpiryDate: '',
  as9100CertNumber: '',
  approvalStatus: 'Unapproved',
  approvalNotes: '',
  contacts: [{ ...blankContact }],
  notes: '',
};

/**
 * AS9120 standing, mirroring domain/supplierScore.js#certificationState so the
 * list and the scorecard agree. 60 days is the usual window to chase a renewal.
 */
function certState(s) {
  if (!s?.as9120CertNumber && !s?.as9120ExpiryDate) return { state: 'Missing', label: 'No AS9120 on file' };
  if (!s.as9120ExpiryDate) return { state: 'Missing', label: 'No expiry date on file' };
  const days = Math.round((new Date(s.as9120ExpiryDate) - Date.now()) / 86400000);
  if (days < 0) return { state: 'Expired', label: `Expired ${Math.abs(days)}d ago` };
  if (days <= 60) return { state: 'ExpiringSoon', label: `Expires in ${days}d` };
  return { state: 'Valid', label: `Valid ${days}d` };
}
const CERT_CHIP = { Valid: 'ready', ExpiringSoon: 'review', Expired: 'risk', Missing: 'muted' };
const APPROVAL_CHIP = {
  Approved: 'ready', Conditional: 'review', Probation: 'review',
  Suspended: 'risk', Unapproved: 'muted',
};

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listSuppliers();
      setItems(r.items);
    } catch (e) {
      setError(e.message || 'Failed to load suppliers');
      toast.push(e.message || 'Failed to load suppliers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openEdit(s) {
    const contacts = (s.contacts && s.contacts.length > 0)
      ? s.contacts.map((c) => ({ name: c.name || '', email: c.email || '', phone: c.phone || '' }))
      : [{ ...blankContact }];
    setForm({
      name: s.name || '',
      category: Array.isArray(s.category) ? s.category : [],
      defaultIncoterm: s.defaultIncoterm || '',
      defaultLocation: s.defaultLocation || '',
      defaultCountryOfOrigin: s.defaultCountryOfOrigin || '',
      as9120CertNumber: s.as9120CertNumber || '',
      // <input type="date"> needs a bare YYYY-MM-DD, not an ISO timestamp.
      as9120ExpiryDate: s.as9120ExpiryDate ? String(s.as9120ExpiryDate).slice(0, 10) : '',
      as9100CertNumber: s.as9100CertNumber || '',
      approvalStatus: s.approvalStatus || 'Unapproved',
      approvalNotes: s.approvalNotes || '',
      contacts,
      notes: s.notes || '',
    });
    setEditingId(s._id);
    setShowNew(true);
  }

  function closeModal() {
    setShowNew(false);
    setEditingId(null);
    setForm(blankForm);
  }

  function updateContact(idx, patch) {
    setForm((f) => ({ ...f, contacts: f.contacts.map((c, i) => i === idx ? { ...c, ...patch } : c) }));
  }
  function addContact() {
    setForm((f) => ({ ...f, contacts: [...f.contacts, { ...blankContact }] }));
  }
  function removeContact(idx) {
    setForm((f) => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));
  }

  async function save() {
    try {
      // Drop fully-blank contact rows; keep only those with at least one field
      const cleanContacts = form.contacts
        .map((c) => ({
          name: c.name.trim() || undefined,
          email: c.email.trim() || undefined,
          phone: c.phone.trim() || undefined,
        }))
        .filter((c) => c.name || c.email || c.phone);

      const body = {
        name: form.name.trim(),
        category: form.category,
        defaultIncoterm: form.defaultIncoterm || undefined,
        defaultLocation: form.defaultLocation.trim() || undefined,
        // Shipping origin drives the landed-cost lane + duty rate (#13).
        defaultCountryOfOrigin: form.defaultCountryOfOrigin.trim().toUpperCase() || undefined,
        // Certification + approval standing (#17).
        as9120CertNumber: form.as9120CertNumber.trim() || undefined,
        as9120ExpiryDate: form.as9120ExpiryDate || undefined,
        as9100CertNumber: form.as9100CertNumber.trim() || undefined,
        approvalStatus: form.approvalStatus || undefined,
        approvalNotes: form.approvalNotes.trim() || undefined,
        notes: form.notes.trim() || undefined,
        contacts: cleanContacts,
      };
      if (editingId) {
        await api.updateSupplier(editingId, body);
        toast.push('Supplier updated');
      } else {
        await api.createSupplier(body);
        toast.push('Supplier created');
      }
      closeModal();
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  const filtered = filter
    ? items.filter((s) =>
        (s.name || '').toLowerCase().includes(filter.toLowerCase())
        || (s.category || []).some((c) => c.toLowerCase().includes(filter.toLowerCase()))
        || (s.defaultLocation || '').toLowerCase().includes(filter.toLowerCase()))
    : items;

  return (
    <Shell
      title="Suppliers"
      subtitle={`${items.length} supplier${items.length === 1 ? '' : 's'}`}
      actions={<button className="btn" onClick={() => setShowNew(true)}>+ Add supplier</button>}
    >
      <input
        type="search"
        className="input"
        placeholder="Filter by name, category, or location…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 12, maxWidth: 360 }}
      />
      <table className="table">
        <thead><tr><th>Name</th><th>Category</th><th>Incoterm</th><th>Ships from</th><th>Approval</th><th>AS9120</th><th>Contacts</th><th></th></tr></thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-500)' }}>Loading suppliers…</td></tr>
          )}
          {!loading && error && (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
              <div className="banner risk" style={{ display: 'inline-flex', textAlign: 'left' }}>
                <div style={{ flex: 1 }}>Couldn’t load suppliers. {error}</div>
                <button className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
              </div>
            </td></tr>
          )}
          {!loading && !error && filtered.length === 0 && (
            <tr>
              <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-500)' }}>
                {filter ? `No suppliers match "${filter}"` : 'No suppliers yet. Add your first supplier.'}
              </td>
            </tr>
          )}
          {filtered.map((s) => {
            const cs = s.contacts || [];
            const primary = cs[0];
            return (
              <tr key={s._id}>
                <td><b>{s.name}</b></td>
                <td>{(s.category || []).join(', ') || '—'}</td>
                <td>{s.defaultIncoterm || '—'}</td>
                <td>{s.defaultCountryOfOrigin || s.defaultLocation || '—'}</td>
                {/* Approval + AS9120 standing (#17) — an expired certificate is an
                    audit finding waiting to happen, so it is visible in the list. */}
                <td>
                  <span className={'chip ' + (APPROVAL_CHIP[s.approvalStatus] || 'muted')} style={{ fontSize: 11 }}>
                    {s.approvalStatus || 'Unapproved'}
                  </span>
                </td>
                <td>
                  {(() => { const c = certState(s); return (
                    <span className={'chip ' + CERT_CHIP[c.state]} style={{ fontSize: 11 }} title={c.label}>
                      {c.state === 'Missing' ? 'No AS9120' : c.label}
                    </span>
                  ); })()}
                </td>
                <td style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  {primary ? (primary.email || primary.name || '—') : '—'}
                  {cs.length > 1 && <span className="chip muted" style={{ marginLeft: 6, fontSize: 11 }}>+{cs.length - 1}</span>}
                </td>
                <td style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                  <Link to={`/suppliers/${s._id}`}>Behavior →</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 className="card-title">{editingId ? 'Edit supplier' : 'Add supplier'}</h2>

            <label className="field"><span className="label">Name *</span>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. SKF Aerospace Distribution"
                autoFocus
              />
            </label>

            <label className="field"><span className="label">Categories</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {CATEGORIES.map((c) => (
                  <span key={c} className={'chip ' + (form.category.includes(c) ? 'primary' : 'muted')} style={{ cursor: 'pointer' }}
                    onClick={() => setForm((f) => ({ ...f, category: f.category.includes(c) ? f.category.filter((x) => x !== c) : [...f.category, c] }))}>
                    {c}
                  </span>
                ))}
              </div>
            </label>

            <div className="row">
              <label className="field"><span className="label">Default incoterm</span>
                <select
                  className="select"
                  value={form.defaultIncoterm}
                  onChange={(e) => setForm((f) => ({ ...f, defaultIncoterm: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {INCOTERMS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </label>
              <label className="field"><span className="label">Default location</span>
                <input
                  className="input"
                  value={form.defaultLocation}
                  onChange={(e) => setForm((f) => ({ ...f, defaultLocation: e.target.value }))}
                  placeholder="e.g. Frankfurt, DE"
                />
              </label>
                  {/* Shipping origin (#13) + certification and approval (#17). */}
                  <label className="field"><span className="label">Ships from (ISO country)</span>
                    <input
                      className="input mono"
                      aria-label="Default country of origin"
                      placeholder="US / GB / SG / DE"
                      maxLength={2}
                      value={form.defaultCountryOfOrigin}
                      onChange={(e) => setForm((f) => ({ ...f, defaultCountryOfOrigin: e.target.value.toUpperCase() }))}
                    />
                  </label>
                  <label className="field"><span className="label">Approval status</span>
                    <select
                      className="select"
                      aria-label="Approval status"
                      value={form.approvalStatus}
                      onChange={(e) => setForm((f) => ({ ...f, approvalStatus: e.target.value }))}
                    >
                      {APPROVAL_STATUSES.map((a) => <option key={a}>{a}</option>)}
                    </select>
                  </label>
                  <label className="field"><span className="label">AS9120 certificate no.</span>
                    <input
                      className="input mono"
                      aria-label="AS9120 certificate number"
                      value={form.as9120CertNumber}
                      onChange={(e) => setForm((f) => ({ ...f, as9120CertNumber: e.target.value }))}
                    />
                  </label>
                  <label className="field"><span className="label">AS9120 expiry</span>
                    <input
                      className="input mono"
                      type="date"
                      aria-label="AS9120 expiry date"
                      value={form.as9120ExpiryDate}
                      onChange={(e) => setForm((f) => ({ ...f, as9120ExpiryDate: e.target.value }))}
                    />
                  </label>
                  <label className="field"><span className="label">AS9100 certificate no.</span>
                    <input
                      className="input mono"
                      aria-label="AS9100 certificate number"
                      value={form.as9100CertNumber}
                      onChange={(e) => setForm((f) => ({ ...f, as9100CertNumber: e.target.value }))}
                    />
                  </label>
                  <label className="field"><span className="label">Approval notes</span>
                    <input
                      className="input"
                      aria-label="Approval notes"
                      placeholder="e.g. conditional pending audit"
                      value={form.approvalNotes}
                      onChange={(e) => setForm((f) => ({ ...f, approvalNotes: e.target.value }))}
                    />
                  </label>
            </div>

            <div style={{ marginTop: 16, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="label">Contacts</span>
              <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>First row is the primary contact (used for dispatch).</span>
            </div>
            {form.contacts.map((c, idx) => (
              <div key={idx} style={{ border: '1px solid var(--border-1)', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <b style={{ fontSize: 13 }}>{idx === 0 ? 'Primary contact' : `Contact ${idx + 1}`}</b>
                  {form.contacts.length > 1 && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeContact(idx)}>Remove</button>
                  )}
                </div>
                <div className="row">
                  <label className="field"><span className="label">Name</span>
                    <input
                      className="input"
                      value={c.name}
                      onChange={(e) => updateContact(idx, { name: e.target.value })}
                      placeholder="Sales / contact name"
                    />
                  </label>
                  <label className="field"><span className="label">Email</span>
                    <input
                      className="input"
                      type="email"
                      value={c.email}
                      onChange={(e) => updateContact(idx, { email: e.target.value })}
                      placeholder="quotes@supplier.com"
                    />
                  </label>
                </div>
                <label className="field"><span className="label">Phone (optional)</span>
                  <input
                    className="input"
                    value={c.phone}
                    onChange={(e) => updateContact(idx, { phone: e.target.value })}
                  />
                </label>
              </div>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" onClick={addContact} style={{ marginBottom: 12 }}>
              + Add another contact
            </button>

            <label className="field"><span className="label">Notes (optional)</span>
              <textarea
                className="input"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. AS9120 certified, prefers EUR pricing…"
              />
            </label>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn" onClick={save} disabled={!form.name.trim()}>{editingId ? 'Save changes' : 'Create supplier'}</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
